import { useRef, useState, type DragEvent, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { BoardDto, ProjectWorkspaceDto, TaskSummaryDto } from '@taqon/contracts';
import { BORDER } from '../../../shared/ui/tokens';
import { CATEGORY_LABELS, columnTasks } from './api';
import { Assignees, DueDate, LabelChip, PriorityChip, Progress } from './parts';

/** Where a task goes: a column, before another task there or (null) at its end. */
export interface MoveTarget {
  readonly columnId: string;
  readonly beforeTaskId: string | null;
}

const DRAG_TYPE = 'application/x-ibms-task';

/**
 * The selected board as columns of cards (CLR-66). Top-level tasks only: a subtask shows as its
 * parent's progress. A card is dragged to another place — dropped on a card it goes before it,
 * dropped on a column's free space it goes to the end — or moved from its menu with the keyboard.
 */
export function Kanban({
  board,
  tasks,
  workspace,
  today,
  onOpen,
  onMove,
  onQuickAdd,
}: {
  board: BoardDto;
  /** The tasks to show, already filtered. */
  tasks: readonly TaskSummaryDto[];
  workspace: ProjectWorkspaceDto;
  today: string;
  onOpen: (taskId: string) => void;
  onMove: (task: TaskSummaryDto, target: MoveTarget) => void;
  onQuickAdd: (columnId: string, title: string) => Promise<unknown>;
}) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const columns = [...board.columns].sort((a, b) => a.sequence - b.sequence);
  const byId = new Map(tasks.map((t) => [t.id, t]));

  // Where a drag started: one that ends within a few pixels of it was a click on a card whose
  // mouse moved a little, and opens the task instead (a draggable card swallows such clicks).
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const drop = (event: DragEvent, target: MoveTarget) => {
    event.preventDefault();
    event.stopPropagation();
    const id = event.dataTransfer.getData(DRAG_TYPE) || dragging;
    setDragging(null);
    setOver(null);
    const task = id === null ? undefined : byId.get(id);
    if (task === undefined || stays(task, target, columnTasks(tasks, target.columnId))) return;
    onMove(task, target);
  };

  return (
    <Box
      role="list"
      aria-label={`Board ${board.name}`}
      sx={{
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: 'minmax(260px, 1fr)',
        gap: 2,
        overflowX: 'auto',
        pb: 1,
      }}
    >
      {columns.map((column) => {
        const cards = columnTasks(tasks, column.id);
        return (
          <Paper
            key={column.id}
            role="listitem"
            aria-label={`${column.name}, ${String(cards.length)} tasks`}
            variant="outlined"
            onDragOver={(event) => {
              if (!workspace.canEdit) return;
              event.preventDefault();
              setOver(column.id);
            }}
            onDragLeave={() => {
              setOver((current) => (current === column.id ? null : current));
            }}
            onDrop={(event) => {
              drop(event, { columnId: column.id, beforeTaskId: null });
            }}
            sx={{
              p: 1.5,
              minHeight: 240,
              bgcolor: over === column.id ? 'action.hover' : 'grey.50',
              borderColor: over === column.id ? 'primary.main' : BORDER,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
                {column.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {CATEGORY_LABELS[column.category]} · {cards.length}
              </Typography>
            </Stack>
            {cards.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                board={board}
                workspace={workspace}
                today={today}
                draggable={workspace.canEdit}
                onOpen={() => {
                  onOpen(task.id);
                }}
                onDragStart={(event) => {
                  event.dataTransfer.setData(DRAG_TYPE, task.id);
                  event.dataTransfer.effectAllowed = 'move';
                  dragStart.current = { x: event.clientX, y: event.clientY };
                  setDragging(task.id);
                }}
                onDragEnd={(event) => {
                  const start = dragStart.current;
                  dragStart.current = null;
                  setDragging(null);
                  setOver(null);
                  if (
                    start !== null &&
                    Math.abs(event.clientX - start.x) < CLICK_SLOP &&
                    Math.abs(event.clientY - start.y) < CLICK_SLOP
                  ) {
                    onOpen(task.id);
                  }
                }}
                onDrop={(event) => {
                  drop(event, { columnId: column.id, beforeTaskId: task.id });
                }}
                onMove={(target) => {
                  onMove(task, target);
                }}
                columnCards={cards}
              />
            ))}
            {workspace.canEdit && (
              <QuickAdd columnName={column.name} onAdd={(title) => onQuickAdd(column.id, title)} />
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

/** A drag that moves less than this many pixels either way was a click. */
const CLICK_SLOP = 6;

/**
 * Whether a drop would leave the task where it is: before itself, before the card that already
 * follows it, or at the end of its own column when it is already last. Nothing is sent then.
 */
export function stays(
  task: TaskSummaryDto,
  target: MoveTarget,
  cards: readonly TaskSummaryDto[],
): boolean {
  if (target.columnId !== task.columnId) return false;
  if (target.beforeTaskId === task.id) return true;
  const index = cards.findIndex((t) => t.id === task.id);
  if (index < 0) return false;
  const next = cards[index + 1];
  return target.beforeTaskId === null ? next === undefined : next?.id === target.beforeTaskId;
}

function TaskCard({
  task,
  board,
  workspace,
  today,
  draggable,
  columnCards,
  onOpen,
  onDragStart,
  onDragEnd,
  onDrop,
  onMove,
}: {
  task: TaskSummaryDto;
  board: BoardDto;
  workspace: ProjectWorkspaceDto;
  today: string;
  draggable: boolean;
  columnCards: readonly TaskSummaryDto[];
  onOpen: () => void;
  onDragStart: (event: DragEvent) => void;
  onDragEnd: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
  onMove: (target: MoveTarget) => void;
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const labels = workspace.labels.filter((l) => task.labelIds.includes(l.id));
  const index = columnCards.findIndex((t) => t.id === task.id);
  const close = () => {
    setMenu(null);
  };
  return (
    <Paper
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      // The whole card opens the task, not only its title; its own buttons keep their action.
      onClick={(event: MouseEvent) => {
        if ((event.target as HTMLElement).closest('button, a, [role="menuitem"]') === null)
          onOpen();
      }}
      onDragOver={(event) => {
        if (draggable) event.preventDefault();
      }}
      onDrop={onDrop}
      sx={{
        position: 'relative',
        p: 1.25,
        cursor: 'pointer',
        borderLeft: 4,
        borderLeftColor: task.priority === 'urgent' ? 'error.main' : 'primary.main',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={`Open ${task.key} ${task.title}`}
        sx={{ display: 'block', width: '100%', textAlign: 'left', pr: 3 }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {task.key}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {task.title}
        </Typography>
      </ButtonBase>
      {draggable && (
        <IconButton
          size="small"
          aria-label={`Move ${task.key}`}
          aria-haspopup="menu"
          onClick={(event) => {
            setMenu(event.currentTarget);
          }}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
        <PriorityChip priority={task.priority} />
        {labels.map((label) => (
          <LabelChip key={label.id} label={label} />
        ))}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Stack spacing={0.25}>
          <DueDate task={task} today={today} />
          <Progress task={task} />
        </Stack>
        <Assignees ids={task.assigneeIds} members={workspace.members} />
      </Stack>
      <Menu anchorEl={menu} open={menu !== null} onClose={close}>
        {index > 0 && (
          <MenuItem
            onClick={() => {
              close();
              onMove({ columnId: task.columnId, beforeTaskId: columnCards[index - 1]?.id ?? null });
            }}
          >
            Move up
          </MenuItem>
        )}
        {index < columnCards.length - 1 && (
          <MenuItem
            onClick={() => {
              close();
              onMove({ columnId: task.columnId, beforeTaskId: columnCards[index + 2]?.id ?? null });
            }}
          >
            Move down
          </MenuItem>
        )}
        {[...board.columns]
          .sort((a, b) => a.sequence - b.sequence)
          .filter((column) => column.id !== task.columnId)
          .map((column) => (
            <MenuItem
              key={column.id}
              onClick={() => {
                close();
                onMove({ columnId: column.id, beforeTaskId: null });
              }}
            >
              Move to {column.name}
            </MenuItem>
          ))}
      </Menu>
    </Paper>
  );
}

/** A one-line "add a task" at the foot of a column: Enter adds, Escape cancels. */
function QuickAdd({
  columnName,
  onAdd,
}: {
  columnName: string;
  onAdd: (title: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open) {
    return (
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => {
          setOpen(true);
        }}
        sx={{ alignSelf: 'flex-start', mt: 'auto' }}
      >
        Add task
      </Button>
    );
  }
  const submit = async () => {
    const value = title.trim();
    if (value === '') return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(value);
      setTitle('');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <TextField
      size="small"
      autoFocus
      label={`New task in ${columnName}`}
      value={title}
      disabled={busy}
      error={error !== null}
      helperText={error ?? 'Enter to add, Escape to close'}
      onChange={(event) => {
        setTitle(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          void submit();
        }
        if (event.key === 'Escape') {
          setOpen(false);
          setTitle('');
        }
      }}
    />
  );
}
